import React, { forwardRef, useMemo, useEffect, useState, useCallback } from 'react';
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import message from 'components/CustomMessage';
import _, { split } from 'lodash';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { Alert, Form } from "antd"

const ACCOUNT_VALUE = "account";

const HOST_VALUE = "host";

const CONTAINER_VALUE = "container"

const CONTAINER_INTEGRATION_TYPE_NAMESPACE = "namespace";
const CONTAINER_INTEGRATION_TYPE_REGISTRY = "registry";

const CONTAINER_INTEGRATION_TYPE_OPTIONS = [
  { label: 'Namespace', value: CONTAINER_INTEGRATION_TYPE_NAMESPACE },
  { label: 'Registry', value: CONTAINER_INTEGRATION_TYPE_REGISTRY },
];

const RADIO_OPTIONS = [
  {
    label: 'Account/Container Registry',
    value: ACCOUNT_VALUE
  },
  {
    label: 'Host',
    value: HOST_VALUE,
  },
  {
    label: 'Container',
    value: CONTAINER_VALUE,
  }
];

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  type: ACCOUNT_VALUE,
  projectList: [{ account: undefined }],
};

const LaceworkMappingForm = forwardRef(function LaceworkMappingForm(props, ref) {
  const featureFlags = useFeatureFlag();
  const isS3FlowEnabled = featureFlags.DEPRECATED_LACEWORK_S3_FLOW_ENABLED;

  const [accounts, setAccounts] = useState(() => {
    if (props.record && props.record.type === ACCOUNT_VALUE) {
      const { account, cloudAccountId, cloudAccountName } = props.record
      const cloudAccountIdArray = split(cloudAccountId, ',');
      const cloudAccountNameArray = split(cloudAccountName, ',');
      return cloudAccountIdArray.map((id, index) => ({ id, name: cloudAccountNameArray[index], otherProperties: { type: 'cloudAccount', account } }))
    }
    return [];
  });

  const [initialValues] = useState(() => {
    if (props.record) {
        return [];
    }
    const defaultType = isS3FlowEnabled ? ACCOUNT_VALUE : HOST_VALUE;
    return [{ ...initialFormValues, type: defaultType }];
  });

  const [registryOptions, setRegistryOptions] = useState(() => {
    if (props.record && props.record.cloudRegistryId && props.record.type === ACCOUNT_VALUE) {
      const { account, cloudRegistryId, cloudRegistryName } = props.record
      const cloudRegistryIdArray = cloudRegistryId.split(',');
      const cloudRegistryNameArray = cloudRegistryName.split(',');
      return cloudRegistryIdArray.map((id, index) => ({ id, name: cloudRegistryNameArray[index], otherProperties: { type: 'container', account } }))
    }
    return [];
  });
  
  const [nameSpaces, setNameSpaces] = useState(() => {
    if (props.record && props.record.type === CONTAINER_VALUE && props.record.podNamespace && props.record.podNamespace !== "All") {
      const podNamespaceArray = props.record.podNamespace.split(',');
      return podNamespaceArray.map(ns => ({ id: ns, name: ns }))
    };
    return [];
  });

  const [containerRegistries, setContainerRegistries] = useState([]);
  const [containerIntegrationType, setContainerIntegrationType] = useState(() => {
    return props.record?.containerIntegrationType;
  });

  const [machineTagOptions, setMachineTagOptions] = useState(() => {
    if (props.record && props.record.type === HOST_VALUE) {
      const { machineTagKey, machineTagValue } = props.record
      return ([{ id: machineTagKey, name: machineTagKey, otherProperties: [{ id: machineTagValue, name: machineTagValue }] }])
    };
    return undefined;
  })

  const [form] = Form.useForm();

  const fetchAccount = useCallback(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      setAccounts(projects.filter(p => p.otherProperties.type === "cloudAccount"));
      setRegistryOptions(projects.filter(p => p.otherProperties.type === "container"))
    }).catch(() => {
      message.error('Problem fetching projects')
    });
    return cancel;
  }, [props.loginConfigId, props.toolName])

  const fetchContainer = useCallback(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, type: CONTAINER_VALUE });
    promise.then(res => setNameSpaces(res.projects))
      .catch(() => message.error("Problem fetching namespaces"));
    return cancel;
  }, [props.loginConfigId, props.toolName])

  const fetchContainerRegistries = useCallback(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, type: 'containerRegistry' });
    promise.then(res => setContainerRegistries(res.projects))
      .catch(() => message.error("Problem fetching container registries"));
    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchHost = useCallback(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, type: HOST_VALUE });
    promise.then(res => setMachineTagOptions(res.projects))
      .catch(() => message.error("Problem fetching Host"));
    return cancel;
  }, [props.loginConfigId, props.toolName]);


  useEffect(() => {
    fetchAccount();
    fetchHost();
  }, [fetchAccount, fetchHost]);

  useEffect(() => {
    if (containerIntegrationType === CONTAINER_INTEGRATION_TYPE_NAMESPACE) {
      return fetchContainer();
    }
    if (containerIntegrationType === CONTAINER_INTEGRATION_TYPE_REGISTRY) {
      return fetchContainerRegistries();
    }
  }, [containerIntegrationType, fetchContainer, fetchContainerRegistries]);

  const getExtraFieldsData = useCallback((config) => {
    const { cloudAccountId, cloudRegistryId, type } = config;
    if (type === ACCOUNT_VALUE) {
      const registryNames = _.flatMap(_.map(cloudRegistryId, r => _.filter(registryOptions, op => op.id === r).map(op => op.name)));
      const accountList = _.flatMap(_.map(cloudAccountId, a => _.filter(accounts, op => op.id === a)));
      const cloudAccountNames = _.uniq(accountList.map(op => op.name));
      const accountNames = accountList.map(op => op.otherProperties?.account);
      return ({
        account: _.isEmpty(accountNames) ? null : accountNames[0],
        cloudAccountName: _.isEmpty(cloudAccountNames) ? null : cloudAccountNames.join(),
        cloudAccountId: _.isEmpty(cloudAccountId) ? null : cloudAccountId.join(),
        cloudRegistryId: _.isEmpty(cloudRegistryId) ? null : cloudRegistryId.join(),
        cloudRegistryName: _.isEmpty(cloudRegistryId) ? null : registryNames.join(),
        type,
      })
    };
    if (type === HOST_VALUE) {
      const { machineTagKey, machineTagValue } = config
      return ({
        machineTagKey,
        machineTagValue,
        type
      });
    };
    if (type === CONTAINER_VALUE) {
      const { containerIntegrationType, podNamespace, containerRegistryName } = config
      if (containerIntegrationType === CONTAINER_INTEGRATION_TYPE_REGISTRY) {
        return ({
          containerIntegrationType,
          containerRegistryName: _.isEmpty(containerRegistryName) ? "All" : containerRegistryName.join(','),
          type
        })
      }
      return ({
        containerIntegrationType,
        podNamespace: _.isEmpty(podNamespace) ? "All" : podNamespace.join(','),
        type
      })
    }
  }, [accounts, registryOptions]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { cloudAccountId, cloudRegistryId, machineTagKey, machineTagValue, type, podNamespace, containerIntegrationType, containerRegistryName } = config;

    return {
      cloudAccountId: cloudAccountId ? cloudAccountId.split(',') : undefined,
      cloudRegistryId: cloudRegistryId ? cloudRegistryId.split(',') : undefined,
      machineTagKey,
      machineTagValue,
      podNamespace: podNamespace && podNamespace !== "All" ? podNamespace.split(',') : undefined,
      containerIntegrationType,
      containerRegistryName: containerRegistryName && containerRegistryName !== "All" ? containerRegistryName.split(',') : undefined,
      type
    }
  }, []);

  const accountFields = useMemo(() => [{
    name: 'cloudAccountId',
    colSpan: '12',
    type: FieldTypes.MULTI_SELECT,
    label: 'Cloud Account',
    placeholder: "Leave blank to select all",
    options: _.uniqBy(_.map(accounts, acc => ({ label: `${acc.name} (${acc.otherProperties.account})`, value: acc.id })), 'value'),
    // disabled: bucketName,
    help: "Please select cloud account from same tenant.",
    props: {
      autoFocus: true,
      loading: _.isUndefined(accounts),
      allowClear: true
    }
  },
  {
    name: "cloudRegistryId",
    colSpan: '12',
    options: _.uniqBy(_.map(registryOptions, reg => ({ label: `${reg.name} (${reg.otherProperties.account})`, value: reg.id })), 'value'),
    type: FieldTypes.MULTI_SELECT,
    label: "Container Registry",
    placeholder: "Leave blank to select all",
    loading: _.isUndefined(registryOptions)
  }], [accounts, registryOptions]);

  const containerRegistryField = useMemo(() => [{
    name: "containerRegistryName",
    label: "Registry",
    colSpan: 12,
    type: FieldTypes.MULTI_SELECT,
    placeholder: "Leave blank to select all",
    options: _.map(containerRegistries, reg => ({ label: reg.name, value: reg.id })),
  }], [containerRegistries]);

  const containerNamespaceField = useMemo(() => {
    return [...(getLoadingMessage(nameSpaces, "Namespaces")),
    {
      name: "podNamespace",
      label: "Namespace",
      colSpan: 12,
      type: FieldTypes.MULTI_SELECT,
      placeholder: "Leave blank to select all",
      options: _.map(nameSpaces, ns => ({ label: ns.name, value: ns.id })),
    }]
  }, [nameSpaces])

  const projectListRender = useCallback((listField) => {
    const selectedType = form.getFieldsValue().configurations[listField.name]?.type;
    let projectField = []
    if (selectedType === ACCOUNT_VALUE) {
      projectField = accountFields;
    }
    else if (selectedType === HOST_VALUE) {
      projectField = [...(getLoadingMessage(machineTagOptions, "Machine Tags")),
      {
        name: 'machineTagKey',
        colSpan: '12',
        label: 'Machine Tag Key',
        required: true,
        type: FieldTypes.SELECT,
        options: machineTagOptions,
        loading: !machineTagOptions,
        props: {
          autoFocus: true,
          onChange: (val, field) => {
            const { name } = field;
            const configurations = form.getFieldValue("configurations");
            configurations[name] = {
              ...configurations[name],
              machineTagValue: undefined
            }
            form.setFieldsValue({ configurations: configurations });
          }
        }
      },
      {
        name: "machineTagValue",
        colSpan: '12',
        label: "Machine Tag Value",
        type: FieldTypes.SELECT,
        props: {
          setExtraProps: (field, listField) => {
            const allConfigs = form.getFieldsValue().configurations;
            const selectedTag = allConfigs[listField.name]?.machineTagKey
            const machineTag = machineTagOptions?.find(i => i.id === selectedTag)
            const machineTagValueOptions = machineTag?.otherProperties?.machineTagValues ? machineTag.otherProperties.machineTagValues : [];
            return {
              options: machineTagValueOptions,
              resetOptionsProp: selectedTag
            }
          }
        }
      }]
    }
    else if (selectedType === CONTAINER_VALUE) {
      const selectedIntegrationType = form.getFieldsValue().configurations[listField.name]?.containerIntegrationType;
      projectField = [
        {
          name: "containerIntegrationType",
          label: "Select Integration Type",
          colSpan: 15,
          type: FieldTypes.SELECT,
          options: CONTAINER_INTEGRATION_TYPE_OPTIONS,
          required: true,
          props: {
            onChange: (value) => {
              setContainerIntegrationType(value);
              const configurations = form.getFieldValue("configurations");
              configurations[listField.name] = {
                ...configurations[listField.name],
                podNamespace: undefined,
                containerRegistryName: undefined,
              };
              form.setFieldsValue({ configurations });
            }
          }
        },
        ...(selectedIntegrationType === CONTAINER_INTEGRATION_TYPE_NAMESPACE ? containerNamespaceField : []),
        ...(selectedIntegrationType === CONTAINER_INTEGRATION_TYPE_REGISTRY ? containerRegistryField : []),
      ];
    }

    return (<div className="m-b-sm">
      {generateFormFields(projectField, 10, listField)}
    </div>)
  }, [accountFields, containerNamespaceField, containerRegistryField, form, machineTagOptions])

  const radioOptions = useMemo(() => {
    let options = RADIO_OPTIONS.filter(opt => !opt.flag || featureFlags[opt.flag]);
    if (!isS3FlowEnabled) {
      options = options.filter(opt => opt.value !== ACCOUNT_VALUE);
    } else {
      options = options.map(opt => opt.value === ACCOUNT_VALUE ? { ...opt, label: 'Account/Container Registry (Deprecated)' } : opt);
    }
    return options.map(opt => ({
      ...opt,
      disabled: (!_.isNull(props.loginConfig.bucketName) && [HOST_VALUE, CONTAINER_VALUE].indexOf(opt.value) !== -1) || (props.record && props.record?.type !== opt.value)
    }));
  }, [featureFlags, isS3FlowEnabled, props.loginConfig.bucketName, props.record]);

  const extraFields = useMemo(() =>
    [
      {
        name: "type",
        type: FieldTypes.RADIO,
        options: radioOptions
      },
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender
      }
    ],
    [projectListRender, radioOptions])


  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      title="Map Lacework Projects"
      {...props}
    />
  )
})

export default LaceworkMappingForm;

const getLoadingMessage = (projects, name) => (
  _.isEmpty(projects) ? [{
    name: '',
    render: () => <Alert type="warning" className='m-b-m' message={`${name} will take some time to load`} />
  }] : []
)